import {londonDay} from './domain.mjs';

const DAY_MS=86400000;
const keyDate=day=>new Date(`${day}T12:00:00Z`);
const dayDistance=(earlier,later)=>Math.round((+keyDate(later)-+keyDate(earlier))/DAY_MS);
const validTime=value=>{
 if(value===undefined || value===null || value==='') return null;
 const ms=+new Date(value);
 return Number.isFinite(ms)?ms:null;
};

/**
 * Derive personal Caravan Trail state from the student's event history.
 *
 * Ordinary review events use existing card IDs. A successful location event is
 * an unhinted Hard/Good/Easy `phrase-reference` recall. An application pass is
 * an `apply` event with result `covered`, choicePassageId in the approved
 * scenario's defensibleIds, and knewWhy === true. Historic covered events do
 * not acquire a marker. An explicit daily event has mode `daily`, cardId
 * `daily:YYYY-MM-DD:v1` and result `done`; a no-due exercise has mode `trail`,
 * cardId `trail:PASSAGE_ID:YYYY-MM-DD:v1`, passageId and result `done`.
 * Validation of those completion events against a real session belongs to the
 * accepting store. Accepted challenge attempts can add markers through the
 * separate challengeAttempts argument without changing scheduling history.
 *
 * `applicationChoices` is the approved choice set, keyed by scenario passageId.
 * No marker is awarded without that independent answer key.
 */
export function projectJourney(passages,cards,events,now=new Date(),applicationChoices=[],challengeAttempts=[]) {
 const nowMs=validTime(now);
 if(nowMs===null) throw new TypeError('now must be a valid date');
 const today=londonDay(nowMs);
 const cardById=new Map(cards.map(card=>[card.id,card]));
 const choiceByPassage=new Map(applicationChoices.map(choice=>[choice.passageId,new Set(choice.defensibleIds||[])]));
 const stops=passages.map(p=>({passageId:p.id,reference:p.reference,title:p.title||p.reference,revealed:false,location:false,application:false,complete:false,points:0}));
 const stopById=new Map(stops.map(stop=>[stop.passageId,stop]));
 const days=new Set(),seen=new Set();

 for(const event of events){
  if(!event || typeof event.id!=='string' || seen.has(event.id) || event.schedulerApplied===false) continue;
  seen.add(event.id);
  const atMs=validTime(event.effectiveAt||event.at);
  if(atMs===null || atMs>nowMs) continue;
  const day=londonDay(atMs);
  if(event.mode==='daily'){
   if(event.result==='done' && event.cardId===`daily:${day}:v1`) days.add(day);
   continue;
  }
  if(event.mode==='trail'){
   const stop=stopById.get(event.passageId);
   if(stop && event.result==='done' && event.cardId===`trail:${event.passageId}:${day}:v1`) stop.revealed=true;
   continue;
  }
  const card=cardById.get(event.cardId);
  if(!card || !stopById.has(card.passageId)) continue;
  const stop=stopById.get(card.passageId);
  if(card.mode==='recall' && Number.isInteger(event.rating) && event.rating>=1 && event.rating<=4){
   stop.revealed=true;
   if(card.kind==='phrase-reference' && !event.hinted && event.rating>=2) stop.location=true;
  }else if(card.mode==='practice' && (event.result==='covered' || event.result==='needs-practice')){
   stop.revealed=true;
   if(card.kind==='apply' && event.result==='covered' && event.knewWhy===true && choiceByPassage.get(card.passageId)?.has(event.choicePassageId)) {
    const chosen=stopById.get(event.choicePassageId);
    if(chosen){chosen.revealed=true;chosen.application=true;}
   }
  }
 }
 // Challenge attempts live outside ordinary review history. Only accepted
 // server-scored passes may add permanent skill markers; they never affect FSRS.
 const seenAttempts=new Set();
 for(const attempt of challengeAttempts){
  if(!attempt || typeof attempt.id!=='string' || seenAttempts.has(attempt.id) || validTime(attempt.acceptedAt)===null || validTime(attempt.acceptedAt)>nowMs) continue;
  seenAttempts.add(attempt.id);
  const prompted=stopById.get(attempt.passageId);
  if(prompted) prompted.revealed=true;
  if(attempt.passed!==true) continue;
  // The class corpus limits class credit, but a defensible passage outside
  // that corpus can still demonstrate the student's personal application.
  const demonstratedId=attempt.skill==='application'?attempt.choicePassageId:attempt.passageId;
  const stop=stopById.get(demonstratedId);
  if(!stop || !['location','application'].includes(attempt.skill)) continue;
  stop.revealed=true;
  stop[attempt.skill]=true;
 }

 let points=0,completedStops=0;
 for(const stop of stops){
  stop.points=Number(stop.location)+Number(stop.application);
  stop.complete=stop.points===2;
  points+=stop.points;
  completedStops+=Number(stop.complete);
 }

 const practiceDayKeys=[...days].sort();
 let run=0,bestStreak=0,lastDay=null,lastBridge=null;
 for(const day of practiceDayKeys){
  const gap=lastDay===null?1:dayDistance(lastDay,day);
  if(gap===1) run++;
  else if(gap===2){
   const missed=new Date(+keyDate(lastDay)+DAY_MS).toISOString().slice(0,10);
   if(lastBridge===null || dayDistance(lastBridge,missed)>=7){run++;lastBridge=missed;}
   else {run=1;lastBridge=null;}
  }else {run=1;lastBridge=null;}
  bestStreak=Math.max(bestStreak,run);
  lastDay=day;
 }
 const daysSinceLast=lastDay===null?Infinity:dayDistance(lastDay,today);
 const currentStreak=daysSinceLast<=1?run:0;
 const recoverableStreak=daysSinceLast===2 && (lastBridge===null || dayDistance(lastBridge,new Date(+keyDate(lastDay)+DAY_MS).toISOString().slice(0,10))>=7) ? run : 0;
 return {stops,points,completedStops,summitReached:stops.length>0 && completedStops===stops.length,
  practiceDays:practiceDayKeys.length,practiceDayKeys,currentStreak,recoverableStreak,bestStreak,todayComplete:days.has(today)};
}

/**
 * The cards a student can practise right now to earn a stop's missing markers:
 * the phrase-reference recall for Location and the stop's own apply scenario for
 * Application. A recall card never seen before uses today's new-card allowance,
 * so it is held back when that allowance is spent.
 */
export function trailStepCards(stop,cards,projection,newRemaining) {
 const card=kind=>cards.find(c=>c.passageId===stop.passageId && c.kind===kind);
 const out=[];let waitingForAllowance=false;
 if(!stop.location){
  const recall=card('phrase-reference');
  if(recall && (projection.byCard[recall.id]?.introduced || newRemaining>0)) out.push(recall);
  else if(recall) waitingForAllowance=true;
 }
 if(!stop.application){const apply=card('apply');if(apply) out.push(apply);}
 return {cards:out,waitingForAllowance};
}
